import { getDomainConfigValue } from '@src/model/config/helpers';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

declare global {
    interface Window {
        Bolt: {
            on: (event: string, eventName: string, callback: () => void) => void;
            BOLT_AD_CLICKED: 'boltAdClicked';
            BOLT_AD_COMPLETE: 'boltAdComplete';
            BOLT_AD_ERROR: 'boltAdError';
            BOLT_AD_IMPRESSION: 'boltAdImpression';
            BOLT_AD_PAUSED: 'boltAdPaused';
            BOLT_AD_REQUEST_START: 'boltAdRequestStart';
            BOLT_AD_SESSION_COMPLETE: 'boltAdSessionComplete';
            BOLT_AD_STARTED: 'boltAdStarted';
            BOLT_FIRST_QUARTILE: 'boltFirstQuartile';
            BOLT_MIDPOINT: 'boltMidPoint';
            BOLT_THIRD_QUARTILE: 'boltThirdQuartile';
            SHOW_HIDDEN_CONTAINER: 'showHiddenContainer';
        };
        ramp: {
            que?: (() => void)[];
            passiveMode?: boolean;
            spaAds: (options: { path: string; countPageView?: boolean; ads: { type: string; selectorId?: string }[] }) => void;
        } & Record<string, unknown>;
    }
}

export interface UsePlaywireRamp {
    isLoaded: boolean;
}

function videoEvents() {
    console.log('Playwire Ramp Video Events Initialized');
    window.Bolt.on('precontent_ad_video', window.Bolt.BOLT_AD_STARTED, () => {
        console.log('BOLT_AD_STARTED - Event Fired');
    });
    window.Bolt.on('precontent_ad_video', window.Bolt.BOLT_AD_COMPLETE, () => {
        console.log('BOLT_AD_COMPLETE - Event Fired');
        document.getElementById('video-preloader')?.remove();

        // Trigger corner_ad_video after pre-content completes
        console.log('[PlaywireRamp] Triggering corner_ad_video after pre-content completion');
        window.ramp.que?.push(() => {
            window.ramp.spaAds({
                ads: [{ type: 'corner_ad_video' }],
                path: location.pathname,
                countPageView: false,
            });
        });
    });
    window.Bolt.on('precontent_ad_video', window.Bolt.BOLT_AD_ERROR, () => {
        console.log('BOLT_AD_ERROR - Event Fired');
        document.getElementById('video-preloader')?.remove();

        // Trigger corner_ad_video after pre-content error
        console.log('[PlaywireRamp] Triggering corner_ad_video after pre-content error');
        window.ramp.que?.push(() => {
            window.ramp.spaAds({
                ads: [{ type: 'corner_ad_video' }],
                path: location.pathname,
                countPageView: false,
            });
        });
    });
    window.Bolt.on('precontent_ad_video', window.Bolt.BOLT_AD_CLICKED, () => {
        console.log('BOLT_AD_CLICKED - Event Fired');
    });
    window.Bolt.on('precontent_ad_video', window.Bolt.BOLT_AD_IMPRESSION, () => {
        console.log('BOLT_AD_IMPRESSION - Event Fired');
    });
    window.Bolt.on('precontent_ad_video', window.Bolt.BOLT_AD_PAUSED, () => {
        console.log('BOLT_AD_PAUSED - Event Fired');
    });
    window.Bolt.on('precontent_ad_video', window.Bolt.BOLT_AD_REQUEST_START, () => {
        console.log('BOLT_AD_REQUEST_START - Event Fired');
        const preloaderContainer = document.getElementById('video-preloader');
        if (!preloaderContainer) return;
        preloaderContainer.className = 'video-tool__video-preloader';
    });
    window.Bolt.on('precontent_ad_video', window.Bolt.BOLT_AD_SESSION_COMPLETE, () => {
        console.log('BOLT_AD_SESSION_COMPLETE - Event Fired');
    });
    window.Bolt.on('precontent_ad_video', window.Bolt.BOLT_FIRST_QUARTILE, () => {
        console.log('BOLT_FIRST_QUARTILE - Event Fired');
    });
    window.Bolt.on('precontent_ad_video', window.Bolt.BOLT_MIDPOINT, () => {
        console.log('BOLT_MIDPOINT - Event Fired');
    });
    window.Bolt.on('precontent_ad_video', window.Bolt.BOLT_THIRD_QUARTILE, () => {
        console.log('BOLT_THIRD_QUARTILE - Event Fired');
    });
    window.Bolt.on('precontent_ad_video', window.Bolt.SHOW_HIDDEN_CONTAINER, () => {
        console.log('SHOW_HIDDEN_CONTAINER - Event Fired');
        document.getElementById('video-preloader')?.remove();
    });
}

const usePlaywireRamp = (): UsePlaywireRamp => {
    const location = useLocation();

    const [isLoaded, setIsLoaded] = useState(false);

    // Get config from domain config or props
    const playWireAdConfig = getDomainConfigValue('playWireAd');
    if (!playWireAdConfig) {
        console.warn('[PlaywireRamp] No Playwire configuration found');
        return { isLoaded: false };
    }
    const { pubId, websiteId } = playWireAdConfig;

    const initPlaywireRamp = () => {
        if (!pubId || !websiteId) {
            console.log('[PlaywireRamp] Missing Publisher Id and Website Id');
            return;
        }
        if (window.ramp?.que) {
            setIsLoaded(true);
            return;
        }

        window.ramp = window.ramp || {};
        window.ramp.que = window.ramp.que || [];
        window.ramp.passiveMode = true;
        window.ramp.onPlayerReady = videoEvents;

        // Load the Ramp configuration script
        const configScript = document.createElement('script');
        configScript.src = `https://cdn.intergient.com/${pubId}/${websiteId}/ramp.js`;

        configScript.onload = () => {
            console.log(`[PlaywireRamp] Ramp component loaded with Publisher ID: ${pubId} and Website ID: ${websiteId}`);
            setIsLoaded(true);
        };

        document.body.appendChild(configScript);
    };

    useEffect(() => {
        initPlaywireRamp();
    }, [location.pathname]);

    return { isLoaded };
};

export default usePlaywireRamp;
